import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchPost } from '@app/src/api/fetchHooks'
import EmptyState from '@app/src/components/EmptyState/EmptyState'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import OverviewCardsSection from '@app/src/pages/ResourceCollection/Collections/ManageRequests/Overview/OverviewCardsSection'
import OverviewCompanyProgressSection from '@app/src/pages/ResourceCollection/Collections/ManageRequests/Overview/OverviewCompanyProgressSection'
import OverviewRequestProgressSection from '@app/src/pages/ResourceCollection/Collections/ManageRequests/Overview/OverviewRequestProgressSection'
import RequestsFilters from '@app/src/pages/ResourceCollection/Filters/RequestsFilters'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { RawExportPayload } from '@app/src/types/filter'
import type { QuestionnaireStatistics } from '@app/src/types/resourceExplorer'
import { br } from '@app/src/utils/translationMarkup'
import { WfRequestTags } from '@app/src/wf-constants'
import { ArrowForwardOutlined, ListAltOutlined } from '@mui/icons-material'
import { Box, Button, CircularProgress, Grid } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface OverviewManageRequestsSceneProps {
  tabs?: ResourceCollectionSceneProps['tabs']
  actionButtons?: ResourceCollectionSceneProps['actionButtons']
  relatedContentFilter?: FilterGroup[]
  setCreateDialogOpen?: () => void
}

const allowedFilters = [
  'template.id',
  'status',
  'periodType',
  'provider.websiteAddress',
  'provider.countryId',
  'provider.vatNumber',
  'provider.id',
  'request.responseExternalStatus',
  'request.responseInternalStatus',
  'isActivated',
  'deadline',
  'createdAt',
  'activatedAt',
  'periodName',
  'provider.providerApprovalStatus',
  'provider.priority',
  'provider.finalRiskRating',
  'provider.activityStatus',
  'provider.providerOwners.userId',
  'provider.providerOwners.user',
  'provider.supplierUsage',
  'provider.tier',
  'provider.categoryOptions.id',
  'template.tags.tag',
  'provider.spends.spendClassification',
  'provider.spends.periodName',
  'provider.assessments.totalLevel',
  'provider.assessments.section1Level',
  'provider.assessments.section2Level',
  'provider.assessments.section3Level',
  'provider.assessments.section4Level',
  'provider.assessments.section5Level',
  'provider.assessments.assessmentTemplate.name',
  'provider.assessments.assessmentTemplate.id',
  'request.subscriptions.assessmentName',
  'request.subscriptions.totalLevel',
  'request.subscriptions.section1Level',
  'request.subscriptions.section2Level',
  'request.subscriptions.section3Level',
  'request.subscriptions.section4Level',
  'request.subscriptions.section5Level',
  'targetType',
  'targetProductId',
  'provider.mappingNodes.actorType.id',
  'provider.mappingNodes.tier',
  'provider.mappingNodes.product.productStatus',
]

const OverviewManageRequestsScene: React.FC<OverviewManageRequestsSceneProps> = ({
  actionButtons,
  relatedContentFilter,
  tabs,
  setCreateDialogOpen,
}) => {
  const { formatMessage } = useIntl()
  const userFilters = useGetApiQueryFilters(allowedFilters)
  const hasWriteAccess = useWriteAccess()

  if (userFilters.some(filter => filter.name.endsWith('Level'))) {
    const baselineAssessmentFilter: FilterGroup = {
      name: 'request.template.tags.tag',
      filters: [
        {
          value: WfRequestTags.PublicRequest3,
          operator: Operators.EqualTo,
        },
      ],
    }
    userFilters.push(baselineAssessmentFilter)
  }

  const { data: overviewStats, isLoading } = useFetchPost<QuestionnaireStatistics, RawExportPayload>({
    key: FetchKey.Request,
    endpoint: endpoints.overviewStatistics,
    payload: {
      filter: userFilters,
    },
  })

  const isEmptyState = !overviewStats?.totalRequests

  return (
    <ResourceCollectionScene
      title={formatMessage({ id: 'navbar.manageRequests' })}
      tabs={tabs}
      actionButtons={actionButtons}
      filter={<RequestsFilters implicitFilters={relatedContentFilter} allowedFilters={allowedFilters} />}
      enableScroll
    >
      {isLoading ? (
        <Box display="flex" alignItems="center" justifyContent="center" width="100%">
          <CircularProgress />
        </Box>
      ) : isEmptyState ? (
        <EmptyState
          iconComponent={ListAltOutlined}
          title={formatMessage({ id: 'table.manageRequests.requestsEmptyState.title' })}
          description={formatMessage({ id: 'table.manageRequests.requestsEmptyState.description' }, { br })}
          sx={{ my: 2 }}
        >
          {setCreateDialogOpen && hasWriteAccess && (
            <Button
              variant="outlined"
              endIcon={<ArrowForwardOutlined fontSize="small" />}
              onClick={setCreateDialogOpen}
            >
              {formatMessage({ id: 'table.manageRequests.requestsEmptyState.action' })}
            </Button>
          )}
        </EmptyState>
      ) : (
        <Grid container spacing={2} sx={{ pr: 10, py: 2 }}>
          <Grid item xs={12}>
            <OverviewCardsSection relatedContentFilter={relatedContentFilter} overviewStats={overviewStats} />
          </Grid>

          <Grid item xs={12} md={6}>
            <OverviewCompanyProgressSection allowedFilters={allowedFilters} />
          </Grid>

          <Grid item xs={12} md={6}>
            <OverviewRequestProgressSection allowedFilters={allowedFilters} userFilters={userFilters} />
          </Grid>
        </Grid>
      )}
    </ResourceCollectionScene>
  )
}

export default OverviewManageRequestsScene
